import { Autocomplete, TextField, CircularProgress, IconButton, Box, Typography, Divider } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { showAlertAction } from '../../store/alerts';
import { SiteDto, fetchSites } from './Services';
import { currentSiteId } from '../../../src/content/applications/slips/PageHeader';

// Special option for "Add New Site" when no sites are available
const ADD_NEW_SITE_OPTION: SiteDto = {
  id: -1,
  siteAddress: 'Add New Site',
  customerName: '',
  customerMobile: '',
  customerId: 0,
  billType: '',
};

interface SiteAutocompleteProps {
  small?: boolean;
  onSiteSelected: (selectedSite: SiteDto | null) => void;  // Callback when a site is selected
  selectedSiteId?: number
  label?: string
  blank?: boolean
  onAddNewSite?: () => void;  // Callback to open Add New Site dialog
  onCloseDialog?: () => void;  // Callback to close the parent dialog
}

const SiteAutocomplete: FC<SiteAutocompleteProps> = ({ small, onSiteSelected, selectedSiteId, label, blank, onAddNewSite, onCloseDialog }) => {
  const [sites, setSites] = useState<SiteDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<SiteDto | null>(null);
  const dispatch = useDispatch(); // Access to dispatch actions

  // Fetch site data from the API
  const fetchOpenSites = () => {
    setLoading(true);
    setError(null);
    fetchSites().then((response) => {
      setLoading(false);
      if (response?.success) {
        setSites(response.data);          
      } else {
        dispatch(showAlertAction({
          message: response?.message,
          severity: 'error',
        }));
      }
    });
  };

  useEffect(() => {
    let siteId = blank ? 0 : currentSiteId 
    if(!siteId) {
      siteId = selectedSiteId
    }
    if(sites && siteId && siteId > 0) {
      const site = sites.find(site => site.id === siteId);
      if(site) {
        setSelectedSite(site)
        handleSiteChange(null, site)
      }
    }    
  }, [sites, currentSiteId, selectedSiteId])

  // Load site data on component mount or when sites list is empty
  useEffect(() => {
    if (sites.length === 0) {
      fetchOpenSites();
    }
  }, [sites.length]);

  // Handle manual reload
  const handleReload = () => {
    fetchOpenSites();
  };

  // Handle site selection
  const handleSiteChange = (event: any, newValue: SiteDto | null) => {
    // Check if "Add New Site" option was selected
    if (newValue?.id === -1) {
      // Close the current dialog if callback provided
      if (onCloseDialog) {
        onCloseDialog();
      }
      // Open Add New Site dialog if callback provided
      if (onAddNewSite) {
        onAddNewSite();
      }
      setSelectedSite(null); // Reset selection
      return;
    }
    // Normal site selection logic
    setSelectedSite(newValue);
    onSiteSelected(newValue);  // Notify parent component about the selection
  };

  // Prepare options array - always include "Add New Site" as the first option
  const options = !loading ? [ADD_NEW_SITE_OPTION, ...sites] : sites;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', pt:0 }}>
      <Autocomplete
        size='small'
        id="site-autocomplete"
        data-cy="site_autocomplete"
        options={options}
        getOptionLabel={(option: SiteDto) => {
          if (option.id === -1) {
            return option.siteAddress; // Just return "Add New Site"
          }
          return `${option.siteAddress} (${option.customerName}${option.customerMobile ? `, ${option.customerMobile}` : ''}, ${option.id})`;
        }}
        renderOption={(props, option) => {
          if (option.id === -1) {
            // Special rendering for "Add New Site" option
            return (
              <Box 
                component="li" 
                {...props} 
                sx={{
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start', 
                  fontWeight: 'bold'
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{
                    width:'100%', 
                    fontWeight: 'bold', 
                    textAlign: 'left',
                    color: 'green'
                  }}
                >
                  {option.siteAddress}
                </Typography>
              </Box>
            );
          }
          // Normal site rendering
          return (
            <>
              <Box component="li" {...props} sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{width:'100%', fontWeight: 'bold', textAlign: 'left'}}>
                  
                 Address: {option.siteAddress}, Site ID:{option.id}
                </Typography>
                <Typography variant="body2" sx={{width:'100%', textAlign: 'left' }}>
                {option.customerName}{option.customerMobile ? `, ${option.customerMobile}` : ''}
                </Typography>
              </Box>
              <Divider/>
            </>
          );
        }}
        
        value={selectedSite}
        onChange={handleSiteChange}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{mt:0}}
            label={label ?? "Select a Site"}
            variant="outlined"
            error={!!error}
            helperText={error || ' '}
            inputProps={{
              ...params.inputProps,
              'data-cy': 'site_autocomplete_input',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false;
          return option.id === value.id;
        }}
        disabled={loading}
        sx={{mt: 0, mr: small ? 1 : 0.5, width: '100%'  }}
      />
      <IconButton
        onClick={handleReload}
        disabled={loading}
        sx={{
          backgroundColor: 'green',
          color: 'white',
          marginLeft: 1, // Space between the autocomplete and button
          height: '37px', // Adjust height for better alignment
          width: '37px',  // Square shape
          borderRadius: 1, // Ensure the button is square-shaped
          mt: small ? 0: 0
        }}
      >
        <RefreshIcon />
      </IconButton>
    </Box>
  );
};

export default SiteAutocomplete;
